import React, { useEffect, useMemo, useRef, useState } from "react";
import { Play } from "../../api/types";
import { apiFetch } from "../../api/client";
import { AuthExpiredError } from "../../api/auth";
import { renderPlayPng } from "../../render/renderPlayPng";
import { formatInsertError } from "../office/insertErrors";
import { insertImagesIntoBoxes, InsertImagePayload } from "../office/insertImage";
import PlayCard from "./PlayCard";

interface Props {
  folderId: string | null;
  searchQuery: string;
  onAuthExpired: () => void;
}

function filterPlays(allPlays: Play[], folderId: string | null, searchQuery: string): Play[] {
  const q = searchQuery.trim().toLowerCase();
  if (q) {
    return allPlays.filter((p) => p.name.toLowerCase().includes(q));
  }
  if (folderId) {
    return allPlays.filter((p) => p.folder_id === folderId);
  }
  return allPlays;
}

export default function PlayGrid({ folderId, searchQuery, onAuthExpired }: Props) {
  const [allPlays, setAllPlays] = useState<Play[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkInserting, setBulkInserting] = useState(false);
  const [bulkMessage, setBulkMessage] = useState<string | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    apiFetch<Play[]>("/play")
      .then((data) => {
        const sorted = [...data].sort((a, b) => a.order_index - b.order_index);
        setAllPlays(sorted);
      })
      .catch((err) => {
        if (err instanceof AuthExpiredError) {
          onAuthExpired();
        } else {
          setError("Failed to load plays.");
        }
      })
      .finally(() => setLoading(false));
  }, [onAuthExpired]);

  const plays = useMemo(
    () => filterPlays(allPlays, folderId, searchQuery),
    [allPlays, folderId, searchQuery]
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Selection is scoped to the currently visible plays: plays hidden by the active
  // folder/search filter are never counted or inserted, so users can't bulk-insert
  // plays they can't see.
  const visiblePlayIds = useMemo(() => new Set(plays.map((p) => p._id)), [plays]);
  const visibleSelectedIds = useMemo(
    () => selectedIds.filter((id) => visiblePlayIds.has(id)),
    [selectedIds, visiblePlayIds]
  );

  function toggleSelect(playId: string) {
    setSelectedIds((prev) =>
      prev.includes(playId) ? prev.filter((id) => id !== playId) : [...prev, playId]
    );
    setBulkMessage(null);
    setBulkError(null);
  }

  function selectAllVisible() {
    setSelectedIds(plays.map((p) => p._id));
    setBulkMessage(null);
    setBulkError(null);
  }

  function clearSelection() {
    setSelectedIds([]);
    setBulkMessage(null);
    setBulkError(null);
  }

  async function handleBulkInsert() {
    const selectedPlays = plays.filter((p) => visibleSelectedIds.includes(p._id));

    if (selectedPlays.length === 0) return;

    setBulkInserting(true);
    setBulkMessage(null);
    setBulkError(null);

    try {
      // Render each play independently so one bad play doesn't abort the whole batch.
      const images: InsertImagePayload[] = [];
      let renderFailures = 0;
      for (const play of selectedPlays) {
        try {
          const rendered = await renderPlayPng(play);
          images.push({
            png: rendered.png,
            width: rendered.width,
            height: rendered.height,
            playId: play._id,
          });
        } catch (err) {
          if (err instanceof AuthExpiredError) throw err;
          console.error("[CHLK render error]", play.name, err);
          renderFailures++;
        }
      }

      if (images.length === 0) {
        setBulkError("Could not render the selected plays. Please try again.");
        return;
      }

      const result = await insertImagesIntoBoxes(images);

      const notes: string[] = [];
      if (result.boxes < images.length) {
        const short = images.length - result.boxes;
        notes.push(`${short} more selected than available box${result.boxes === 1 ? "" : "es"}`);
      }
      if (result.failed > 0) notes.push(`${result.failed} failed to insert`);
      if (renderFailures > 0) notes.push(`${renderFailures} failed to render`);

      const base = `Inserted ${result.inserted} play${result.inserted === 1 ? "" : "s"}`;
      setBulkMessage(notes.length > 0 ? `${base} (${notes.join("; ")}).` : `${base}.`);

      setSelectedIds([]);
    } catch (err) {
      console.error("[CHLK bulk insert error]", err);
      if (err instanceof AuthExpiredError) {
        onAuthExpired();
      } else {
        setBulkError(formatInsertError(err));
      }
    } finally {
      setBulkInserting(false);
    }
  }

  if (loading) return <div className="play-grid-empty">Loading plays…</div>;
  if (error) return <div className="play-grid-empty error-text">{error}</div>;

  return (
    <div className="play-grid-panel">
      {visibleSelectedIds.length > 0 && (
        <div className="selection-bar">
          <span className="selection-count">{visibleSelectedIds.length} selected</span>
          <div className="selection-actions">
            <button
              className="btn-selection-primary"
              onClick={handleBulkInsert}
              disabled={bulkInserting}
            >
              {bulkInserting ? "Inserting…" : `Insert ${visibleSelectedIds.length} plays`}
            </button>
            <button className="btn-selection" onClick={selectAllVisible} disabled={bulkInserting}>
              Select all
            </button>
            <button className="btn-selection" onClick={clearSelection} disabled={bulkInserting}>
              Clear
            </button>
          </div>
        </div>
      )}
      {bulkMessage && <div className="bulk-banner success">{bulkMessage}</div>}
      {bulkError && <div className="bulk-banner error">{bulkError}</div>}
      {plays.length === 0 ? (
        <div className="play-grid-empty">No plays found.</div>
      ) : (
        <div className="play-grid">
          {plays.map((play) => (
            <PlayCard
              key={play._id}
              play={play}
              selected={selectedSet.has(play._id)}
              onToggleSelect={() => toggleSelect(play._id)}
              onAuthExpired={onAuthExpired}
            />
          ))}
        </div>
      )}
    </div>
  );
}
